import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Card, CardHeader, CardBody, CardTitle } from '../../../styles/components/ModernCard';
import { FormGroup, FormLabel, FormInput, Divider, Flex } from '../../../styles/components';

// Subtle border glow animation
const borderGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.1), 0 0 5px rgba(99, 102, 241, 0.1) inset; }
  50% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), 0 0 10px rgba(99, 102, 241, 0.15) inset; }
  100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.1), 0 0 5px rgba(99, 102, 241, 0.1) inset; }
`;

// Input focus animation
const inputFocus = keyframes`
  0% { border-color: rgba(99, 102, 241, 0.3); }
  50% { border-color: rgba(99, 102, 241, 0.7); }
  100% { border-color: rgba(99, 102, 241, 0.3); }
`;

const DateInputGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.primary ? 'rgba(79, 70, 229, 0.85)' : 'rgba(15, 23, 42, 0.3)'};
  color: ${props => props.primary ? 'white' : props.theme.colors.primary};
  border: ${props => props.primary ? '1px solid rgba(99, 102, 241, 0.5)' : '1.5px solid rgba(99, 102, 241, 0.6)'};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  backdrop-filter: blur(8px);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${props => props.primary 
        ? 'rgba(129, 140, 248, 0.2) 0%, rgba(99, 102, 241, 0.2) 50%, rgba(79, 70, 229, 0.1) 100%'
        : 'rgba(255, 255, 255, 0.1) 0%, rgba(99, 102, 241, 0.1) 50%, rgba(15, 23, 42, 0.1) 100%'}
    );
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: -100%;
    left: -100%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transition: all 0.6s ease;
    z-index: 1;
  }
  
  &:hover {
    background-color: ${props => props.primary ? 'rgba(79, 70, 229, 0.95)' : 'rgba(99, 102, 241, 0.15)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.primary 
      ? '0 8px 25px -5px rgba(79, 70, 229, 0.5), 0 0 0 2px rgba(129, 140, 248, 0.3)'
      : '0 7px 20px -5px rgba(31, 38, 135, 0.3), 0 0 0 2px rgba(99, 102, 241, 0.1)'};
    
    &:after {
      top: 100%;
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 1rem;
  }
`;

// Space-themed styled components
const SpaceCard = styled(Card)`
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: ${borderGlow} 8s infinite ease-in-out;
  position: relative;
  z-index: 1;
  
  /* Space background gradients */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(124, 58, 237, 0.03) 0%, transparent 50%);
    border-radius: inherit;
    z-index: -1;
  }
  
  /* Star-like dots in background */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(1px 1px at 15% 15%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 35% 45%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
      radial-gradient(1px 1px at 55% 25%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 75% 65%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
      radial-gradient(1.5px 1.5px at 90% 10%, rgba(255, 255, 255, 0.7) 1px, transparent 0);
    border-radius: inherit;
    z-index: -1;
    opacity: 0.3;
  }
`;

const SpaceCardHeader = styled(CardHeader)`
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  padding: 1.25rem 1.5rem;
`;

const SpaceCardTitle = styled(CardTitle)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  letter-spacing: 0.5px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.7),
      rgba(124, 58, 237, 0.5)
    );
    border-radius: 2px;
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.5);
  }
`;

const SectionTitle = styled.h3`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.15rem;
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.4px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 30px;
    height: 1.5px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.6),
      rgba(124, 58, 237, 0.4)
    );
  }
`;

const SectionText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
  line-height: 1.5;
  letter-spacing: 0.2px;
`;

const CycleInfo = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin-bottom: 1.25rem;
  line-height: 1.5;
  letter-spacing: 0.2px;
  
  strong {
    color: rgba(99, 102, 241, 0.9);
    font-weight: 600;
  }
`;

const SpaceDivider = styled(Divider)`
  margin: 2.25rem 0;
  background: linear-gradient(
    to right,
    transparent,
    rgba(99, 102, 241, 0.3),
    transparent
  );
  height: 1px;
  opacity: 0.6;
`;

const StyledFormGroup = styled(FormGroup)`
  label {
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
    letter-spacing: 0.3px;
  }
  
  input {
    background: rgba(15, 23, 42, 0.3);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: rgba(99, 102, 241, 0.6);
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
      animation: ${inputFocus} 2s infinite ease-in-out;
      outline: none;
    }
  }
`;

const SettingsPanel = ({ 
  isEditingCycle, 
  cycleStartDate, 
  cycleEndDate, 
  formatDate,
  onStartDateChange, 
  onEndDateChange, 
  onSaveCycle, 
  onCancelEdit, 
  onStartEdit,
  onArchiveData,
  onGenerateReport,
  onBackupSystem
}) => {
  return (
    <SpaceCard>
      <SpaceCardHeader>
        <SpaceCardTitle>System Settings</SpaceCardTitle>
      </SpaceCardHeader>
      <CardBody>
        <SectionTitle>Internship Cycle Management</SectionTitle>
        <SectionText>Set the start and end dates for the current internship cycle.</SectionText>
        
        {isEditingCycle ? (
          <>
            <DateInputGroup>
              <StyledFormGroup>
                <FormLabel>Cycle Start Date</FormLabel>
                <FormInput 
                  type="date" 
                  value={cycleStartDate}
                  onChange={(e) => onStartDateChange(e.target.value)}
                />
              </StyledFormGroup>
              
              <StyledFormGroup>
                <FormLabel>Cycle End Date</FormLabel>
                <FormInput 
                  type="date" 
                  value={cycleEndDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                />
              </StyledFormGroup>
            </DateInputGroup>
            
            <Flex gap="1rem">
              <ActionButton primary onClick={onSaveCycle}>Save Cycle</ActionButton>
              <ActionButton onClick={onCancelEdit}>Cancel</ActionButton>
            </Flex>
          </>
        ) : (
          <>
            <CycleInfo><strong>Current Cycle:</strong> {formatDate(cycleStartDate)} to {formatDate(cycleEndDate)}</CycleInfo>
            <ActionButton primary onClick={onStartEdit}>Edit Cycle Dates</ActionButton>
          </>
        )}
        
        <SpaceDivider />
        
        <SectionTitle>System Maintenance</SectionTitle>
        <SectionText>Perform system maintenance tasks and operations.</SectionText>
        <Flex gap="1rem" flexWrap="wrap">
          <ActionButton onClick={onArchiveData}>Archive Old Data</ActionButton>
          <ActionButton onClick={onGenerateReport}>Generate System Report</ActionButton>
          <ActionButton onClick={onBackupSystem}>Backup System</ActionButton>
        </Flex>
      </CardBody>
    </SpaceCard>
  );
};

export default SettingsPanel;
